import { supabase } from '@/lib/supabaseClient';
import type { SmsConfig, SmsVerifyResult, SmsMessage, SmsSendResult, SmsTestResult } from '@/types/sms';
import type { RealtimeChannel } from '@supabase/supabase-js';

const PYTHON_BACKEND_URL = import.meta.env.VITE_PYTHON_BACKEND_URL || 'http://localhost:8000';

async function getAuthHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session?.access_token}`,
  };
}

/**
 * Load SMS config for a tenant from the tenants table.
 * The encrypted auth_token arrives in the JSONB but is stripped here.
 * It's Fernet-encrypted (useless without the backend SECRET_KEY).
 */
export async function loadSmsConfig(tenantId: string): Promise<{ config: SmsConfig | null; authTokenSet: boolean }> {
  const { data, error } = await supabase
    .from('tenants')
    .select('sms_config')
    .eq('tenant_id', tenantId)
    .single();

  if (error) throw error;

  const raw = data?.sms_config;
  if (!raw) return { config: null, authTokenSet: false };

  // Track whether an auth token exists, then strip it from the config
  const hasToken = !!raw.auth_token;

  return {
    config: {
      ...raw,
      auth_token: '', // Always strip — never expose encrypted token to UI state
    } as SmsConfig,
    authTokenSet: hasToken,
  };
}

/**
 * Save Twilio SMS configuration via Python backend (handles encryption).
 */
export async function saveSmsConfig(
  tenantId: string,
  config: {
    account_sid: string;
    auth_token?: string; // Only included if changed
    phone_number: string;
    messaging_service_sid?: string;
    enabled: boolean;
  }
): Promise<{ success: boolean; error?: string }> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${PYTHON_BACKEND_URL}/api/v1/sms/save-config`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tenant_id: tenantId, sms_config: config }),
  });

  const result = await response.json();

  if (!response.ok || result.status === 'error') {
    return { success: false, error: result.message || 'Failed to save SMS configuration' };
  }

  return { success: true };
}

/**
 * Verify Twilio credentials by calling the Twilio API via Python backend.
 */
export async function verifySmsConfig(tenantId: string): Promise<SmsVerifyResult> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${PYTHON_BACKEND_URL}/api/v1/sms/verify-config`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tenant_id: tenantId }),
  });

  const result = await response.json();

  if (!response.ok) {
    return { valid: false, error: result.message || 'Verification failed' };
  }

  return result as SmsVerifyResult;
}

// ─── Messaging ───────────────────────────────────────────────────────────────

/**
 * Fetch SMS messages for a project, ordered by creation time.
 */
export async function fetchSmsMessages(
  projectId: string,
  options?: { limit?: number; before?: string }
): Promise<SmsMessage[]> {
  let query = supabase
    .from('sms_messages')
    .select(`
      *,
      sender_user:users!sms_messages_sender_user_id_fkey(first_name, last_name, email)
    `)
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (options?.before) {
    query = query.lt('created_at', options.before);
  }

  if (options?.limit) {
    query = query.limit(options.limit);
  }

  const { data, error } = await query;
  if (error) throw error;

  return (data || []) as unknown as SmsMessage[];
}

/**
 * Send an SMS message via the Python backend.
 * Backend handles Twilio delivery + inserting the record into sms_messages.
 */
export async function sendSms(
  projectId: string,
  tenantId: string,
  message: string
): Promise<SmsSendResult> {
  const headers = await getAuthHeaders();

  let response: Response;
  try {
    response = await fetch(`${PYTHON_BACKEND_URL}/api/v1/sms/send`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        project_id: projectId,
        tenant_id: tenantId,
        message,
      }),
    });
  } catch (networkError) {
    console.error('[smsService] Network error sending SMS:', networkError);
    return { success: false, error: 'Could not reach SMS server. Please check your connection and try again.' };
  }

  let result: Record<string, unknown>;
  try {
    result = await response.json();
  } catch {
    console.error('[smsService] Non-JSON response from SMS backend:', response.status, response.statusText);
    return { success: false, error: `SMS server returned an unexpected response (${response.status})` };
  }

  if (!response.ok || result.status === 'error') {
    const detail = (result.detail as string) || (result.message as string) || `SMS send failed (${response.status})`;
    console.error('[smsService] SMS send error:', detail);
    return { success: false, error: detail };
  }

  return {
    success: true,
    message_sid: result.message_sid as string,
    delivery_status: (result.delivery_status as SmsSendResult['delivery_status']),
  };
}

/**
 * Mark all unread inbound messages for a project as read.
 */
export async function markSmsMessagesRead(projectId: string): Promise<void> {
  const { error } = await supabase
    .from('sms_messages')
    .update({ is_read: true })
    .eq('project_id', projectId)
    .eq('is_read', false)
    .eq('direction', 'inbound');

  if (error) console.error('Error marking SMS messages as read:', error);
}

/**
 * Get count of unread inbound SMS messages for a project.
 */
export async function getUnreadSmsCount(projectId: string): Promise<number> {
  const { count, error } = await supabase
    .from('sms_messages')
    .select('id', { count: 'exact', head: true })
    .eq('project_id', projectId)
    .eq('is_read', false)
    .eq('direction', 'inbound');

  if (error) {
    console.error('Error getting unread SMS count:', error);
    return 0;
  }

  return count || 0;
}

/**
 * Subscribe to real-time SMS message inserts for a project.
 * Returns the channel so the caller can unsubscribe on unmount.
 */
export function subscribeSmsMessages(
  projectId: string,
  onNewMessage: (message: SmsMessage) => void
): RealtimeChannel {
  const channel = supabase
    .channel(`sms-messages-${projectId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'sms_messages',
        filter: `project_id=eq.${projectId}`,
      },
      (payload) => {
        onNewMessage(payload.new as SmsMessage);
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'sms_messages',
        filter: `project_id=eq.${projectId}`,
      },
      (payload) => {
        // Delivery status updates
        onNewMessage(payload.new as SmsMessage);
      }
    )
    .subscribe();

  return channel;
}

/**
 * Send a test SMS to a given phone number via the Python backend.
 */
export async function sendTestSms(
  tenantId: string,
  toPhone: string
): Promise<SmsTestResult> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${PYTHON_BACKEND_URL}/api/v1/sms/send-test`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ tenant_id: tenantId, to_phone: toPhone }),
  });

  const result = await response.json();

  if (!response.ok || result.status === 'error') {
    return { success: false, error: result.message || 'Failed to send test SMS' };
  }

  return { success: true, message_sid: result.message_sid };
}

/**
 * Check if SMS is configured and enabled for a tenant.
 */
export async function isSmsEnabled(tenantId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('tenants')
    .select('sms_config')
    .eq('tenant_id', tenantId)
    .single();

  if (error || !data?.sms_config) return false;
  return data.sms_config.enabled === true && !!data.sms_config.account_sid;
}
